import { useCallback } from 'react';

import { HapticFeedbackType, useVibration } from './useVibration';

/**
 * Options for cart vibration hook
 */
interface UseCartVibrationOptions {
  /**
   * Whether the product has multibuy promo
   */
  isMultibuy: boolean;

  /**
   * Required quantity to activate multibuy discount
   */
  requiredQuantity?: number;

  /**
   * Current quantity in cart
   */
  currentCount: number;
}

/**
 * Hook return type
 */
interface UseCartVibrationReturn {
  /**
   * Trigger vibration when changing cart item count
   * @param newCount - New quantity after change
   * @param action - 'add' or 'remove'
   */
  vibrateOnChange: (newCount: number, action: 'add' | 'remove') => void;
}

/**
 * Calculate vibration type based on multibuy progress
 */
const calculateMultibuyVibrationType = (
  currentCount: number,
  previousCount: number,
  requiredQuantity: number,
): HapticFeedbackType => {
  if (requiredQuantity <= 1 || currentCount <= 0) {
    return 'impactLight';
  }

  const wasActivated = previousCount < requiredQuantity;
  const isActivated = currentCount >= requiredQuantity;

  if (isActivated && wasActivated) {
    return 'notificationSuccess';
  }

  return 'impactLight';
};

/**
 * Centralized hook for cart vibration feedback
 *
 * Combines all cart-related vibration logic in one place:
 * - Regular add/remove: 'impactLight' - light impact feedback
 * - Multibuy activation: 'notificationSuccess' - success notification
 * - Remove to zero: 'rigid' - strong feedback for item removal
 *
 * @example
 * ```tsx
 * const { vibrateOnChange } = useCartVibration({
 *   isMultibuy: Boolean(product.getLowestMultibuyLevel()?.quantity),
 *   requiredQuantity: product.getLowestMultibuyLevel()?.quantity,
 *   currentCount: cartItemsCount,
 * });
 *
 * // In handler:
 * vibrateOnChange(newCount, 'add');
 * ```
 */
export const useCartVibration = ({
  isMultibuy,
  requiredQuantity,
  currentCount,
}: UseCartVibrationOptions): UseCartVibrationReturn => {
  const { vibrate } = useVibration();

  const vibrateOnChange = useCallback(
    (newCount: number, action: 'add' | 'remove') => {
      if (action === 'remove' && newCount === 0) {
        vibrate('rigid');
        return;
      }

      if (isMultibuy && requiredQuantity) {
        const vibrationType = calculateMultibuyVibrationType(
          newCount,
          currentCount,
          requiredQuantity,
        );
        vibrate(vibrationType);
        return;
      }

      vibrate('impactLight');
    },
    [isMultibuy, requiredQuantity, currentCount, vibrate],
  );

  return {
    vibrateOnChange,
  };
};
